import { EventEmitter } from 'events'
import Dispatcher, { IAction, IActionPayload } from '../Dispatcher'
import {
    SETTINGS_AUTOSAVE_CHANGED,
    SETTINGS_BOARD_FOCUS_OFF,
    SETTINGS_BOARD_FOCUS_ON,
    SETTINGS_INFO_HIDDEN,
    SETTINGS_INFO_VISIBLE,
    SETTINGS_LOADED,
    SETTINGS_SET_COOKIE_CONSENT,
} from './SettingsActions'
import { Consent, Visibility } from '../../common/Enums'
import { isLocalStorageAvailable } from '../../repositories/LocalStorageUtilities'

/**
 * Represents the persistable state of the settings store
 */
export interface ISettingsStoreMemento {
    autoSave: boolean
    focus: IFocusSettings
    infoState: Visibility
    cookieConsent: Consent
}

/**
 * Represents the settings state for the focus feature
 */
export interface IFocusSettings {
    isFocused: boolean
    boardId: number
}

/**
 * Represents the possible states of the settings store
 */
enum SettingsStoreState {
    Uninitialized,
    Loaded,
}

/**
 * Flux Store for Settings
 */
class SettingsStore extends EventEmitter {
    private static instance: SettingsStore
    private readonly CHANGE_EVENT = 'SETTINGS_STORE_CHANGED'
    private autoSave: boolean
    private focus: IFocusSettings
    private state: SettingsStoreState
    private infoState: Visibility = Visibility.Visible
    private cookieConsent: Consent = Consent.Declined
    private dispatcherToken: string

    private constructor() {
        super()
        this.autoSave = false
        this.focus = { isFocused: false, boardId: -1 }
        this.state = SettingsStoreState.Uninitialized
        this.dispatcherToken = Dispatcher.register((action: IAction) => {
            switch (action.actionType) {
                case SETTINGS_LOADED:
                    this._Memento = (action as IActionPayload<ISettingsStoreMemento>).payload
                    this.state = SettingsStoreState.Loaded
                    this.emitChange()
                    break
                case SETTINGS_AUTOSAVE_CHANGED:
                    this._AutoSave = (action as IActionPayload<boolean>).payload
                    this.emitChange()
                    break
                case SETTINGS_BOARD_FOCUS_ON:
                    const boardId = (action as IActionPayload<number>).payload
                    this._Focus = { isFocused: true, boardId: boardId }
                    this.emitChange()
                    break
                case SETTINGS_BOARD_FOCUS_OFF:
                    this._Focus = { isFocused: false, boardId: -1 }
                    this.emitChange()
                    break
                case SETTINGS_INFO_VISIBLE:
                    this._InfoState = Visibility.Visible
                    this.emitChange()
                    break
                case SETTINGS_INFO_HIDDEN:
                    this._InfoState = Visibility.Hidden
                    this.emitChange()
                    break
                case SETTINGS_SET_COOKIE_CONSENT:
                    this._CookieConsent = (action as IActionPayload<Consent>).payload
                    this.emitChange()
                    break
            }
        })
    }

    /**
     * Singleson instance accessor
     */
    static GetInstance(): SettingsStore {
        if (!this.instance) this.instance = new SettingsStore()
        return this.instance
    }

    /**
     * Exposes the current autosave state
     */
    get AutoSave(): boolean {
        return this.autoSave
    }

    /**
     * Sets the autosave property
     */
    private set _AutoSave(value: boolean) {
        this.autoSave = value ?? false
    }

    /**
     * Exposes the current focus state
     */
    get Focus(): IFocusSettings {
        return this.focus
    }

    /**
     * Sets the focus property
     */
    private set _Focus(value: IFocusSettings) {
        this.focus = value
    }

    /**
     * Exposes the current settings store state
     */
    get State(): SettingsStoreState {
        return this.state
    }

    /**
     * Exposes the dispatcher token
     */
    get DispatcherToken(): string {
        return this.dispatcherToken
    }

    /**
     * Exposes the store state as a memento
     */
    get Memento(): ISettingsStoreMemento {
        return {
            autoSave: this.autoSave,
            focus: { ...this.focus },
            infoState: this.infoState,
            cookieConsent: this.cookieConsent,
        }
    }

    /**
     * Restores state from memento
     */
    private set _Memento(value: ISettingsStoreMemento) {
        this._AutoSave = value.autoSave
        this._Focus = { ...value.focus }
        this._InfoState = value.infoState
        this._CookieConsent = value.cookieConsent
    }

    /**
     * Exposes the visibility state of the info panel
     */
    get InfoState(): Visibility {
        return this.infoState
    }

    /**
     * Sets the visibility state of the info panel
     */
    private set _InfoState(value: Visibility) {
        this.infoState = value ?? Visibility.Visible
    }

    /**
     * Exposes the users decision on cookie consent.
     */
    get CookieConsent(): Consent {
        return this.cookieConsent
    }

    /**
     * Sets the users decision on cookie consent
     */
    private set _CookieConsent(value: Consent) {
        this.cookieConsent = value ?? Consent.Undecided
    }

    /**
     * Returns true if local storage is available and
     * the user has consented to cookies.
     *
     * Otherwise false
     */
    get CanUseLocalStorage(): boolean {
        return (
            isLocalStorageAvailable() && this.CookieConsent === Consent.Accepted
        )
    }

    /**
     * Adds an event listener which will be invoked
     * when a setting in the store changes
     * @param callback Callback to invoke
     */
    addChangeListener(callback: () => void) {
        this.on(this.CHANGE_EVENT, callback)
    }

    /**
     * Removes an event listener which would otherwise be triggered
     * when the settings in the store change
     * @param callback Callback to remove from listeners
     */
    removeChangeListener(callback: () => void) {
        this.removeListener(this.CHANGE_EVENT, callback)
    }

    /**
     * Invoked to emit the change event
     */
    emitChange() {
        this.emit(this.CHANGE_EVENT)
    }
}

export { SettingsStore as default, SettingsStoreState }
